import cn from 'classnames';
import { Dispatch, useCallback, useState } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect, useDispatch } from 'react-redux';
import { isValid, max, min } from 'date-fns';

import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { createKnmErknmFormEventSelectOption } from 'CorePackage/CatalogsModule/common/createKnmErknmFormEventSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { PM_SUSPENSION_EVENTS } from 'CorePackage/MainModule/routes/routes';
import { IKnmErknmFormEventSelectOption } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IKnmErknmFormEventSelectOption';
import { IKnmErknmFormEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSuspensionEvent } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeCompleted,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { getPeriodSuspensionEventBlockId } from 'KnmPmPackage/MainModule/constants/knmPmFieldsElementsIds';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import {
  ADD_SUSPENSION_ERROR_BY_NOT_FILLED_ACT,
  ADD_SUSPENSION_ERROR_BY_NOT_SIGNED_ACT,
} from 'CorePackage/MainModule/constants/notices';
import { getPmStartDate, getPmStopDate } from 'KnmPmPackage/MainModule/selectors/knmPmSelectors';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import styles from './PmSuspensionEvents.module.scss';
import { PmSuspensionEventsAttachments } from './PmSuspensionEventsAttachments';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IPmSuspensionEventsProps {
  className?: string;
  watchOnly: boolean;
  eventsPm: IKnmErknmFormEvent[] | undefined;
  suspensionEvents?: IKnmErknmFormSuspensionEvent[];
}

interface IPmSuspensionEventsStoreProps {
  formRef: IKnmFormRef | undefined;
  isKnmStatusFillInProcess: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isKnmStatusTypeCompleted: boolean;
  isSupervisor: boolean;
  isNeedActSaveOrSign?: boolean;
  startDatePm: string | undefined;
  stopDatePm: string | undefined;
}

function filterEventOptions(
  events: IKnmErknmFormEventSelectOption[],
  types: string[],
): IKnmErknmFormEventSelectOption[] {
  return events.filter(event => event.type && types.includes(event.type.title));
}

const PmSuspensionEventsWithoutStore = ({
  formRef,
  watchOnly,
  className,
  eventsPm,
  isSupervisor,
  isKnmStatusFillInProcess,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCompleted,
  suspensionEvents,
  isNeedActSaveOrSign = false,
  startDatePm,
  stopDatePm,
}: IPmSuspensionEventsProps & IPmSuspensionEventsStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const eventOptions = eventsPm?.map((value, index) => createKnmErknmFormEventSelectOption(value, index)) || [];
  const filteredEventOptions = filterEventOptions(eventOptions, [
    'Экспертиза',
    'Испытание',
    'Инструментальное обследование',
  ]);

  const isFilledSuspensionEvents = suspensionEvents?.every(suspensionEvent => {
    return (
      suspensionEvent.attachments?.length &&
      suspensionEvent.decisionDateTime != null &&
      // suspensionEvent.decisionNumber != null &&
      suspensionEvent.event != null &&
      suspensionEvent.startDate != null &&
      suspensionEvent.stopDate != null
    );
  });

  const isVisible =
    filteredEventOptions.length > 0 &&
    (isKnmStatusFillInProcess ||
      isKnmStatusTypeWaitingCarryOut ||
      isKnmStatusTypeCarriedOut ||
      isKnmStatusTypeCompleted);
  const isEnable = isSupervisor && !watchOnly && isVisible && isKnmStatusTypeWaitingCarryOut && !isNeedActSaveOrSign;
  const isAddEnable = isEnable && isFilledSuspensionEvents;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const formFieldName = 'suspensionEvents' as keyof IKnmPmForm;

  const addSuspensionEvent = useCallback(() => {
    if (!watchOnly && push) {
      const act = formRef?.current?.getFieldState('act')?.value;

      if (act && (act.isNew || !act.signed)) {
        return dispatch(
          addNoticeAction({
            renderContent: act?.isNew ? ADD_SUSPENSION_ERROR_BY_NOT_FILLED_ACT : ADD_SUSPENSION_ERROR_BY_NOT_SIGNED_ACT,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ACT_ERRORS,
          }),
        );
      }

      const addingPeriodSuspension: IKnmErknmFormSuspensionEvent = { isNew: true };

      push(formFieldName, addingPeriodSuspension);
    }
  }, [push, watchOnly, formRef]);

  const makeRemoveSuspensionEventHandler = (index: number) => () =>
    !watchOnly && remove && remove(formFieldName, index);

  if (!isVisible) {
    return null;
  }

  const parseStartDate = parseDate({
    date: startDatePm || new Date() || '',
    parseFormat: DATE_FORMAT_LONG_FNS,
  });

  return (
    <div className={cn(styles.Wrapper, className)} id={PM_SUSPENSION_EVENTS}>
      <div>
        <div className="shared-group-label_margin">
          Сведения о приостановлении срока проведения ПМ
          <span className="shared-no-wrap-text">
            <Button
              className="shared-btn-add"
              text="Добавить"
              variant="link"
              color="primary"
              type="button"
              id="pmSuspensionEventsAddButton"
              disabled={!isAddEnable}
              onClick={addSuspensionEvent}
            />
          </span>
        </div>
      </div>
      <FieldArray name={formFieldName}>
        {({ fields }: FieldArrayRenderProps<IKnmErknmFormSuspensionEvent, HTMLElement>) => {
          if (!fields.length) {
            return null;
          }

          // const getBlockLabel = (suspensionEventFieldValue: IKnmErknmFormSuspensionEvent) => {
          //   if (suspensionEventFieldValue.decisionNumber) {
          //     return (
          //       <>
          //         <span>Решение о приостановлении&nbsp;</span>
          //         <span className="shared-group-label">№{suspensionEventFieldValue.decisionNumber}</span>
          //       </>
          //     );
          //   }
          //
          //   return 'Решение о приостановлении';
          // };

          const pmSuspensionEventFieldsValue: IKnmErknmFormSuspensionEvent[] = fields.value;

          return (
            <ul className={styles.PeriodList}>
              {fields.map((suspensionEventFieldName, sIndex) => {
                const pmSuspensionEventFieldValue: IKnmErknmFormSuspensionEvent =
                  pmSuspensionEventFieldsValue?.[sIndex];
                const id = getPeriodSuspensionEventBlockId(`${sIndex}`);
                const { guid, attachments, decisionDateTime } = pmSuspensionEventFieldValue;

                const isWatchOnly = !isEnable || !!guid;

                const hasError = checkHasErrorInCollapseBlock(formRef, suspensionEventFieldName);

                const parseStartDateSuspension = parseDate({
                  date: decisionDateTime || new Date() || '',
                  parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                });

                const path = `${suspensionEventFieldName}.startDate` as keyof IKnmForm;
                const startDateEventField = (formRef as IKnmFormRef)?.current.getFieldState(path)?.value;

                /**
                 * Ограничение max date поля "Дата начала приостановления" минимальной из дат
                 * окончания всего ПМ и окончания текущего действия
                 */
                const matchedPmEvent = eventsPm?.find(
                  e => e.type?.title === pmSuspensionEventFieldValue.event?.type?.label,
                );
                const parseStopDatePm = parseDate({ date: stopDatePm || '', parseFormat: DATE_FORMAT_LONG_FNS });
                const parseEventStopDate = parseDate({
                  date: matchedPmEvent?.stopDate || '',
                  parseFormat: DATE_FORMAT_LONG_FNS,
                });
                const startDateMaxDates = [parseStopDatePm, parseEventStopDate].filter((date): date is Date =>
                  isValid(date),
                );
                const startDateMaxDate = startDateMaxDates.length ? min(startDateMaxDates) : undefined;

                let baseStopDate = parseStartDateSuspension;

                if (startDateEventField) {
                  const parseEventStartDateString = parseDate({
                    date: startDateEventField?.toString(),
                    parseFormat: DATE_FORMAT_LONG_FNS,
                  });

                  baseStopDate = max(
                    [parseStartDateSuspension, parseEventStartDateString].filter((date): date is Date => isValid(date)),
                  );
                }

                return (
                  <KnmCollapse
                    key={id}
                    // title={getBlockLabel(pmSuspensionEventFieldValue)}
                    title={'Решение о приостановлении'}
                    isNew={!guid}
                    openByDefault={!guid}
                    onClose={!guid ? makeRemoveSuspensionEventHandler(sIndex) : undefined}
                    id={id}
                    changeOpenedCallback={e =>
                      setKnmCollapseOpened(state => ({
                        ...state,
                        [suspensionEventFieldName]: e,
                      }))
                    }
                    error={hasError && knmCollapseOpened[suspensionEventFieldName]}
                  >
                    <div>
                      <Row align="center" id={`${suspensionEventFieldName}.attachments`}>
                        <PmSuspensionEventsAttachments
                          formFieldName={formFieldName}
                          isWatchOnly={isWatchOnly}
                          sIndex={sIndex}
                          suspensionEventFieldName={suspensionEventFieldName}
                          attachments={attachments}
                          formRef={formRef}
                        />
                      </Row>
                      <Row notWrap align="center">
                        <FieldBlock size="col-4" id={`${suspensionEventFieldName}.event`}>
                          <div className="shared-field-row-block_width">
                            <label className="shared-field-label_row" htmlFor="event">
                              Действие
                            </label>
                          </div>
                        </FieldBlock>

                        <SelectInputField
                          className={styles.EventFieldBlock}
                          name={`${suspensionEventFieldName}.event`}
                          options={filteredEventOptions}
                          disabled={isWatchOnly}
                        />
                      </Row>
                      <Row notWrap>
                        <FieldBlock size="col-4">
                          <div className="shared-field-row-block_width">
                            <label className={cn('shared-field-label_row', styles.Label)} htmlFor="decisionDateTime">
                              Дата и время решения
                            </label>
                          </div>
                        </FieldBlock>

                        <FieldBlock
                          className={styles.EventFieldBlock}
                          id={`${suspensionEventFieldName}.decisionDateTime`}
                        >
                          <DatePickerField
                            name={`${suspensionEventFieldName}.decisionDateTime`}
                            disabled={isWatchOnly}
                            timeFormat
                            minDate={parseStartDate}
                          />
                        </FieldBlock>
                        {/*
                        <FieldBlock size="col-4">
                          <div className="shared-field-row-block_width">
                            <label className={cn('shared-field-label_row', styles.Label)} htmlFor="decisionNumber">
                              Номер решения
                            </label>
                          </div>
                        </FieldBlock>

                        <FieldBlock size="col-4" id={`${suspensionEventFieldName}.decisionNumber`}>
                          <TextInputField name={`${suspensionEventFieldName}.decisionNumber`} disabled={isWatchOnly} />
                        </FieldBlock>
                        */}
                      </Row>
                      <Row notWrap align="center">
                        <FieldBlock size="col-4">
                          <div className="shared-field-row-block_width">
                            <label className={cn('shared-field-label_row', styles.Label)} htmlFor="startDate">
                              Дата начала <br /> приостановления срока ПМ
                            </label>
                          </div>
                        </FieldBlock>

                        <FieldBlock size="col-4" id={`${suspensionEventFieldName}.startDate`}>
                          <DatePickerField
                            name={`${suspensionEventFieldName}.startDate`}
                            disabled={isWatchOnly}
                            minDate={parseStartDateSuspension}
                            maxDate={startDateMaxDate}
                          />
                        </FieldBlock>

                        <FieldBlock size="col-4">
                          <div className="shared-field-row-block_width">
                            <label className={cn('shared-field-label_row', styles.Label)} htmlFor="stopDate">
                              Дата окончания <br /> приостановления срока ПМ
                            </label>
                          </div>
                        </FieldBlock>

                        <FieldBlock size="col-4" id={`${suspensionEventFieldName}.stopDate`}>
                          <DatePickerField
                            name={`${suspensionEventFieldName}.stopDate`}
                            disabled={isWatchOnly}
                            minDate={baseStopDate}
                          />
                        </FieldBlock>
                      </Row>
                      <CheckboxField
                        id={`${suspensionEventFieldName}.isWorkDaysOnly`}
                        name={`${suspensionEventFieldName}.isWorkDaysOnly`}
                        disabled={isWatchOnly}
                        size="medium"
                        type="checkbox"
                        theme="system"
                        className="shared-field-row_margin"
                        label="При перерасчёте даты окончания ПМ и действия учитывать только рабочие дни"
                      />
                    </div>
                  </KnmCollapse>
                );
              })}
            </ul>
          );
        }}
      </FieldArray>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IPmSuspensionEventsStoreProps => ({
  formRef: getKnmFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusFillInProcess: getKnmIsErknmStatusFillInProcess(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCompleted: getKnmIsErknmStatusTypeCompleted(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  startDatePm: getPmStartDate(state),
  stopDatePm: getPmStopDate(state),
});

export const PmSuspensionEvents = connect(mapStateToProps)(PmSuspensionEventsWithoutStore);
